<template>
  <a-modal
    id="show-video-modal"
    v-model:visible="showStream"
    :title="deviceName + ' Streaming'"
    :footer="null"
    destroy-on-close
    centered
    width="1000px"
    :mask-closable="false"
    :get-popup-container="() => document.getElementById('stream-modal-body')"
    @cancel="showStream = false"
  >
    <div id="stream-modal-body" class="w-100">
      <VideoStream
        :serialNumber="device.Serial_number"
        :deviceSettings="deviceSettings"
        @closeModal="showStream = false"
      />
    </div>
  </a-modal>

  <a-card
    size="small"
    class="station-card cursor-pointer"
    :class="{
      'active-card': isActive,
      'idle-card': isActive && isIdle,
    }"
  >
    <!-- Card Header -->
    <div class="card-header">
      <div class="device-title-section">
        <div class="device-name-wrapper">
          <a-typography-title
            :level="4"
            class="device-name"
            @click="redirectToPage('Operation Level Analytics')"
          >
            {{
              deviceName.length > 20
                ? deviceName.slice(0, 20) + '...'
                : deviceName
            }}
          </a-typography-title>
          <a-rate
            v-if="device.starred"
            :value="1"
            :count="1"
            class="star-rating"
          />
        </div>

        <!-- Status Badge -->
        <div class="status-badge">
          <a-tag :color="isActive ? 'success' : 'default'" class="status-tag">
            <template #icon>
              <global-outlined v-if="isActive" />
              <custom-icon v-else :icon="$customIcons.OfflineInternetIcon" />
            </template>
            {{ isActive ? 'Online' : 'Offline' }}
          </a-tag>
        </div>
      </div>

      <!-- Action Icons -->
      <div class="action-icons">
        <a-popover arrow hover placement="bottom">
          <span class="action-icon info-icon">
            <InfoOutlined />
          </span>
          <template #content>
            <div
              v-for="row in deviceHoverInfo"
              :key="row.key"
              class="hoverInfo"
            >
              <span v-if="row.key === 'Organization'">
                {{ row.title }} :
                <a-tag color="processing">
                  {{ device[row.key].org_name_to_display || organization }}
                </a-tag>
              </span>
              <span v-else-if="row.key === 'version_no'">
                {{ row.title }} :
                <a-tag color="processing">
                  {{ deviceSettings?.version_no || '-' }}
                </a-tag>
              </span>
              <span v-else>
                {{ row.title }} :
                <a-tag color="processing">{{ device[row.key] || '-' }}</a-tag>
              </span>
            </div>
          </template>
        </a-popover>

        <a-tooltip
          :title="device.archive_device ? 'Unarchive Device' : 'Archive Device'"
        >
          <span class="action-icon archive-icon" @click="handleArchive">
            <EyeFilled />
          </span>
        </a-tooltip>
      </div>
    </div>

    <template #actions v-if="isActive">
      <div class="d-flex justify-content-center">
        <a-button
          type="link"
          :disabled="analyticsTraceDisable"
          style="flex: 1; border: none; box-shadow: none"
          @click="redirectToPage('Operation Level Analytics')"
        >
          <template #icon>
            <i class="bi bi-box-arrow-up-right mr-2" />
          </template>
          Analytics
        </a-button>

        <a-divider type="vertical" style="height: 30px" />

        <a-button
          type="link"
          :disabled="analyticsTraceDisable"
          style="flex: 1; border: none; box-shadow: none"
          @click="redirectToPage('Trace')"
        >
          <template #icon>
            <i class="bi bi-box-arrow-up-right mr-2" />
          </template>
          Trace
        </a-button>
      </div>
    </template>

    <!-- Card Content -->
    <div class="card-content">
      <div class="mt-1">
        <!-- Device Activity data/tags and stream icon -->
        <div class="tags-wrapper center flex-wrap">
          <a-tooltip title="View Live Stream">
            <i
              v-if="isActive"
              class="bi bi-cast strem-icon"
              @click="showStream = true"
            />
          </a-tooltip>

          <a-tag
            :color="isActive ? 'processing' : 'default'"
            style="font-size: 14px"
            class="d-flex align-items-center"
          >
            <template #icon>
              <global-outlined v-if="isActive" />
              <custom-icon v-else :icon="$customIcons.OfflineInternetIcon" />
            </template>
            {{ isActive ? 'Online' : 'Offline' }}
          </a-tag>

          <span
            v-if="isActive && isIdle"
            class="text-danger inference-activity"
          >
            Idle
          </span>

          <div
            v-if="deviceSettings?.isInferenceRunning"
            class="inference-activity"
          >
            <span :class="{ 'text-danger': !deviceSettings?.recordInference }">
              Trace {{ deviceSettings?.recordInference ? 'On' : 'Off' }}
            </span>
            <span
              :class="{ 'text-secondary': !deviceSettings?.isInferenceRunning }"
            >
              Guidance Mode
            </span>
          </div>

          <div
            v-if="
              deviceSettings?.recordInference &&
              !deviceSettings?.isInferenceRunning
            "
            :class="{ 'text-secondary': !deviceSettings?.recordInference }"
            class="inference-activity"
          >
            Trace {{ deviceSettings?.recordInference ? 'On' : 'Off' }}
          </div>

          <div
            v-if="deviceSettings?.record_shift"
            class="text-success font-weight-bold mt-1"
          >
            Recording
          </div>
          <span
            v-if="
              Object.keys(deviceSettings?.running_schedule || {}).length !== 0
            "
            class="mr-2 schedule-indicator"
          >
            <a-tooltip title="Schedule Running">
              <CalendarOutlined class="icon-style" />
            </a-tooltip>
          </span>
        </div>

        <div v-if="isActive && currentTask" class="task-wrapper">
          <label>Current Operation:</label>
          <span class="text-primary">&nbsp; {{ currentTask }}</span>
          <!-- <span v-else class="text-muted">None</span> -->
        </div>
      </div>

      <!-- Device Cycle -->
      <div class="device-cycles-info">
        <div
          v-if="!deviceSettings?.record_shift"
          class="w-100 center flex-column my-2 middle-card"
        >
          <strong>{{ getActivityData('number_of_cycles') }}</strong>
          <span>Cycles Performed (Today)</span>
        </div>

        <div
          v-if="deviceSettings?.record_shift"
          class="w-100 center flex-column my-2 middle-card"
        >
          <strong>{{ getActivityData('hours_recorded') }}</strong>
          <span>Hours Recorded</span>
        </div>

        <a-row
          v-if="!deviceSettings?.record_shift"
          :gutter="8"
          class="w-100 mt-2 d-flex align-items-start"
        >
          <a-col
            v-for="(card, i) in smallCardConfig"
            :key="card.key"
            :span="(i + 1) % 2 === 0 ? 12 : 6"
            class="center flex-column small-card"
          >
            <strong>{{ getActivityData(card.key) }}</strong>
            <span class="text-center">{{ card.title }} </span>
          </a-col>
        </a-row>

        <!-- <label
          >Cycles Count <small class="font-weight-bold">(last 24hrs)</small>
        </label>
        <ul class="cycle-list count-list">
          <li>
            <label>Total Cycles:</label>
            <span>{{ getActivityData('number_of_cycles') }}</span>
          </li>
          <li>
            <label>Good Cycles:</label>
            <span>{{ getActivityData('good_cycles') }}</span>
          </li>
          <li>
            <label>Bad Cycles:</label>
            <span>{{ getActivityData('bad_cycles') }}</span>
          </li>
        </ul> -->

        <!-- <label
          >Cycles Time
          <small class="font-weight-bold">(last 24hrs)</small></label
        >
        <ul class="cycle-list time-list">
          <li>
            <label>Average Cycles Time:</label>
            <span>{{ getActivityData('average_cycle_time') }}</span>
          </li>
          <li>
            <label>Total Cycles Time:</label>
            <span>{{ getActivityData('total_cycles_time') }}</span>
          </li>
        </ul> -->
      </div>
    </div>
  </a-card>
</template>
<script>
import deviceHoverInfo from '@/config/device-hover-info-config.js';
import DeviceService from '@/services/device.js';
import {
  CalendarOutlined,
  EyeFilled,
  GlobalOutlined,
  InfoOutlined,
} from '@ant-design/icons-vue';
import dayjs from 'dayjs';
import { dateTimeFormat } from 'src/config/date-format-config.js';
import { mapGetters } from 'vuex';
import VideoStream from './VideoStream.vue';

export default {
  components: {
    VideoStream,
    InfoOutlined,
    GlobalOutlined,
    EyeFilled,
    CalendarOutlined,
  },
  props: {
    device: {
      type: Object,
      required: true,
      default: () => ({}),
    },

    activeDevices: {
      type: Object,
      required: true,
      default: () => ({}),
    },

    activeDevicesSettings: {
      type: Array,
      required: true,
      default: () => [],
    },

    idToTaskNameMapping: {
      type: Object,
      required: true,
      default: () => ({}),
    },

    idToDeviceSerialNoMapping: {
      type: Object,
      required: true,
      default: () => ({}),
    },

    activeDevicesStatus: {
      type: Object,
      required: true,
      default: () => ({}),
    },
  },
  setup() {
    return {
      deviceHoverInfo,
      smallCardConfig: [
        { key: 'bad_cycles', title: 'Bad cycles' },
        { key: 'average_value_add_percentage', title: 'Value add percentage' },
        { key: 'takt_grade', title: 'Takt grade' },
      ],
    };
  },
  data() {
    return {
      deviceSettings: null,
      showStream: false,
      deviceActivity: {
        isInferenceRunning: false,
        recordInference: false, //trace,
        record_shift: false,
      },
    };
  },

  computed: {
    ...mapGetters(['organization']),
    deviceName() {
      return this.device.display_name || this.device.Serial_number;
    },
    currentTask() {
      if (!this.deviceSettings) return '';
      return this.idToTaskNameMapping[this.deviceSettings?.Task];
    },
    isActive() {
      return this.activeDevices[this.device.Serial_number]?.active;
    },
    isIdle() {
      if (!this.deviceSettings || !Object.keys(this.deviceSettings)?.length)
        return false;
      return Object.keys(this.deviceActivity).every(
        (activity) => this.deviceSettings[activity] === false
      );
    },
    analyticsTraceDisable() {
      const cyclesPerformed = this.getActivityData('number_of_cycles');
      return !this.isActive || !cyclesPerformed || cyclesPerformed === '-';
    },
  },

  watch: {
    activeDevices(dict) {
      if (!dict || !Object.keys(dict).length) return;
      this.deviceSettings = this.activeDevices[this.device.Serial_number];
    },
    // activeDevicesSettings(value) {
    //   if (!Object.keys(value).length) return;
    //   this.deviceSettings = value.find(
    //     ({ Device: deviceId }) => deviceId === this.device.Device
    //   );
    // }
  },

  methods: {
    getActivityData(attributeName) {
      if (!Object.keys(this.activeDevicesStatus).length) return '-';
      const deviceSno = this.device.Serial_number;
      const task = this.device.Task;
      const deviceStatus = this.activeDevicesStatus?.[`${deviceSno}${task}`];

      if (!this.isActive || !deviceStatus) {
        if (
          attributeName == 'number_of_cycles' ||
          attributeName == 'bad_cycles'
        ) {
          return 0;
        }
        return '-';
      }
      if (
        deviceStatus &&
        attributeName === 'takt_grade' &&
        deviceStatus['takt_percentage']
      ) {
        const takt_percent = deviceStatus['takt_percentage'].split('.')[0];
        return Number(takt_percent) > 0 ? deviceStatus['takt_grade'] : '-';
      } else if (deviceStatus && deviceStatus[attributeName]) {
        return deviceStatus[attributeName] === '0%'
          ? '-'
          : deviceStatus[attributeName];
      } else return '-';
    },

    getDateTime(date) {
      return `${dayjs(date).format(dateTimeFormat)}`;
    },

    redirectToPage(name) {
      // const d1 = dayjs(new Date().toISOString());
      // const d2 = d1.subtract(24, 'hour');
      if (!this.isActive) {
        return;
      }

      const d1 = dayjs().startOf('day');
      const d2 = dayjs().endOf('day');

      const query = {
        taskId: this.deviceSettings?.Task,
        deviceId: JSON.stringify([this.device.Serial_number]),
        startTime: this.getDateTime(d1),
        endTime: this.getDateTime(d2),
      };

      let { href } = this.$router.resolve({ name, query });
      window.open(href, '_blank');
    },

    async handleArchive() {
      // console.log('this.device: ', this.device);
      // return;
      try {
        // Call the API to archive the device using DeviceService
        const [error] = await DeviceService.archiveDevicePatch(
          this.device.Device,
          { archive_device: !this.device.archive_device },
          false // spinner = false
        );

        if (error) {
          this.$message.error(
            `Failed to ${
              this.device.archive_device ? 'unarchive' : 'archive'
            } device: ${error.message || 'Unknown error'}`
          );
        } else {
          const isArchiving = !this.device.archive_device;
          // this.$message.success(
          //   `${this.deviceName} has been ${
          //     isArchiving ? 'archived' : 'unarchived'
          //   } successfully`
          // );

          // Emit appropriate event based on the action with updated device data
          const updatedDevice = {
            ...this.device,
            archive_device: !this.device.archive_device,
          };
          if (isArchiving) {
            this.$emit('device-archived', updatedDevice);
          } else {
            this.$emit('device-unarchived', updatedDevice);
          }
        }
      } catch (error) {
        console.error('Error archiving device:', error);
        this.$message.error('Failed to archive device. Please try again.');
      }
    },
  },
};
</script>

<style>
/* Modern Card Styling */
.station-card {
  margin-bottom: 24px;
  width: 100%;
  min-height: 320px;
  margin-top: 6px;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border: 1px solid #e9ecef;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  position: relative;
}

.station-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
  border-color: #1890ff;
}

.station-card .ant-card-body {
  padding: 24px;
  box-sizing: border-box;
  height: 100%;
}

.station-card .ant-card-actions {
  border-radius: 0 0 16px 16px;
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
}

.station-card .ant-card-actions > li,
.station-card .ant-card-actions > li span:first-child {
  height: 100%;
}

/* Card Header */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.device-title-section {
  flex: 1;
}

.device-name-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.device-name {
  margin: 0 !important;
  color: #1a1a1a;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.2s ease;
}

.device-name:hover {
  color: #1890ff;
}

.star-rating {
  margin: 0;
}

.status-badge {
  margin-top: 8px;
}

.status-tag {
  border-radius: 20px;
  font-weight: 500;
  padding: 4px 12px;
}

/* Action Icons */
.action-icons {
  display: flex;
  gap: 8px;
  align-items: center;
}

.action-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: rgba(24, 144, 255, 0.1);
  color: #1890ff;
}

.action-icon:hover {
  background: rgba(24, 144, 255, 0.2);
  transform: scale(1.05);
}

.info-icon {
  background: rgba(82, 196, 26, 0.1);
  color: #52c41a;
}

.info-icon:hover {
  background: rgba(82, 196, 26, 0.2);
}

.archive-icon {
  background: rgba(245, 34, 45, 0.1);
  color: #f5222d;
}

.archive-icon:hover {
  background: rgba(245, 34, 45, 0.2);
}

/* Card Content */
.card-content {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

/* Activity Indicators */
.activity-indicators {
  margin-bottom: 20px;
}

.indicator-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.stream-icon {
  cursor: pointer;
  color: #1890ff;
  font-size: 18px;
  transition: all 0.2s ease;
}

.stream-icon:hover {
  color: #40a9ff;
  transform: scale(1.1);
}

.idle-indicator {
  color: #ff4d4f;
  font-weight: 500;
  padding: 4px 8px;
  background: rgba(255, 77, 79, 0.1);
  border-radius: 12px;
  font-size: 12px;
}

.inference-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 500;
}

.inference-indicator > span:first-child::after {
  content: '';
  height: 12px;
  width: 1px;
  background: #d9d9d9;
  display: inline-block;
  margin: 0 6px;
}

.recording-indicator {
  color: #52c41a;
  font-weight: 600;
  padding: 4px 8px;
  background: rgba(82, 196, 26, 0.1);
  border-radius: 12px;
  font-size: 12px;
}

.schedule-indicator {
  display: flex;
  align-items: center;
}

.schedule-icon {
  font-size: 16px;
  color: #faad14;
  cursor: pointer;
  transition: color 0.2s ease;
}

.schedule-icon:hover {
  color: #ffc53d;
}

.current-task {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(24, 144, 255, 0.05);
  border-radius: 8px;
  margin-top: 8px;
}

.task-label {
  font-size: 12px;
  color: #666;
  font-weight: 500;
}

.task-name {
  font-size: 12px;
  color: #1890ff;
  font-weight: 600;
}

/* Performance Metrics */
.performance-metrics {
  margin-top: auto;
}

.main-metric {
  text-align: center;
  padding: 16px;
  background: linear-gradient(135deg, #f0f8ff 0%, #e6f7ff 100%);
  border-radius: 12px;
  margin-bottom: 16px;
  border: 1px solid #bae7ff;
}

.metric-value {
  font-size: 32px;
  font-weight: 700;
  color: #1890ff;
  line-height: 1;
  margin-bottom: 4px;
}

.metric-label {
  font-size: 12px;
  color: #666;
  font-weight: 500;
}

.sub-metrics {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.sub-metric {
  text-align: center;
  padding: 12px 8px;
  background: #fafafa;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
}

.sub-metric-value {
  font-size: 18px;
  font-weight: 600;
  color: #262626;
  line-height: 1;
  margin-bottom: 4px;
}

.sub-metric-label {
  font-size: 10px;
  color: #8c8c8c;
  font-weight: 500;
}

/* Card Actions */
.card-actions {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px 0;
}

.action-button {
  flex: 1;
  height: 40px;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.action-button:hover {
  background: rgba(24, 144, 255, 0.1);
  color: #1890ff;
}

.action-divider {
  height: 24px;
  margin: 0 16px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .station-card {
    min-height: 280px;
  }

  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .action-icons {
    align-self: flex-end;
  }

  .sub-metrics {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* Active/Idle States */
.active-card {
  border-color: #52c41a;
  box-shadow: 0 4px 20px rgba(82, 196, 26, 0.15);
}

.active-card:hover {
  box-shadow: 0 12px 40px rgba(82, 196, 26, 0.25);
}

.idle-card {
  border-color: #ff4d4f;
  box-shadow: 0 4px 20px rgba(255, 77, 79, 0.15);
}

.idle-card:hover {
  box-shadow: 0 12px 40px rgba(255, 77, 79, 0.25);
}

/* Utility Classes */
.cursor-pointer {
  cursor: pointer;
}

.text-underline {
  text-decoration: underline;
}

/* Hover Info Styling */
.hoverInfo span {
  margin: 4px 0;
  display: block;
}

/* Legacy Support */
.center {
  display: flex;
  align-items: center;
  justify-content: center;
}

.tags-wrapper .ant-tag {
  margin: 0 6px;
  margin-top: 0px;
}

.tags-wrapper i {
  cursor: pointer;
  color: #34495e;
  transform: scale(1.4);
  margin: 0px 10px;
}

.tags-wrapper i:hover {
  color: #2391ff;
}

.device-cycles-info {
  width: 100%;
}

.task-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 12px;
  font-size: 15px;
}

.task-wrapper label {
  margin: 0;
  font-weight: 500;
  color: #1f1f1f;
}

.task-wrapper span {
  display: flex;
  flex-wrap: wrap;
  text-align: center;
  line-height: 18px;
}

.inference-activity {
  /* margin-top: 5px; */
  /* font-weight: 500; */
}

.inference-activity > span:first-child::after {
  content: '';
  height: 14px;
  width: 1.5px;
  background: #1f1f1f;
  display: inline-block;
  margin: 0 3px 0 6px;
  margin-bottom: -1.4px;
}

.small-card span,
.small-card strong,
.middle-card span {
  font-size: 16px;
}

.small-card strong,
.middle-card strong {
  /* font-size: 28px; */
  font-weight: 500;
}

.middle-card strong {
  font-size: 28px;
}

.icon-style {
  font-size: 14px;
  color: #ffc107;
  margin-left: 8px;
}
</style>
